/**
 * CaixaComprovantePrintService
 *
 * Gera HTML térmico 80mm e dispara impressão silenciosa de comprovantes
 * de operações de caixa (abertura, suprimento, sangria, fechamento) no
 * PDV Desktop.
 *
 * Estratégia:
 *   1. Renderer gera HTML e persiste em caixa_local.comprovante_html.
 *   2. Renderer chama IPC `printer:imprimir-comprovante-caixa` que usa
 *      a impressora padrão configurada (mesma da NFC-e).
 *   3. Falha de impressão NUNCA desfaz a operação — só retorna ok=false.
 *
 * Reimpressão usa o HTML salvo quando ele já está no modelo atual e
 * atualiza automaticamente comprovantes legados antes de imprimir.
 */
import { getElectronAPI } from "@/plugins/desktop";
import CaixaLocalRepository, { CAIXA_TIPO } from "@/modules/PdvDesktop/repositories/CaixaLocalRepository";
import AppConfigRepository from "@/modules/PdvDesktop/repositories/AppConfigRepository";

const COMPROVANTE_TEMPLATE_VERSION = "4";

// ── Helpers ─────────────────────────────────────────────────────────────────

function _escape(str) {
  if (str == null) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function _brl(valor) {
  const n = Number(valor) || 0;
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function _formatarData(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return _escape(iso);
  return d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

function _formatarCnpj(c) {
  if (!c) return "—";
  const s = String(c).replace(/\D/g, "");
  if (s.length !== 14) return _escape(c);
  return s.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
}

/**
 * Wrapper HTML com CSS térmico 80mm. Inclui media print.
 */
function _wrap(titulo, corpo) {
  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="utf-8" />
<meta name="caixa-comprovante-template" content="${COMPROVANTE_TEMPLATE_VERSION}" />
<title>${_escape(titulo)}</title>
<style>
  @page { size: 80mm auto; margin: 2mm 5mm 2mm 3mm; }
  * { box-sizing: border-box; }
  html, body { color: #000 !important; opacity: 1 !important; }
  body { font-family: 'Courier New', Courier, monospace; font-size: 12px;
         font-weight: 700; margin: 0; padding: 0; width: 72mm; line-height: 1.45;
         -webkit-print-color-adjust: exact; print-color-adjust: exact;
         text-rendering: geometricPrecision; }
  h1, h2, h3 { margin: 0; }
  .cc-title { text-align: center; font-size: 15px; font-weight: 900;
              text-transform: uppercase; letter-spacing: 1px;
              border: 2px dashed #000; padding: 5px 2px; margin: 6px 0; }
  .cc-row { display: flex; justify-content: space-between; gap: 8px; margin: 2px 0; }
  .cc-row b { font-weight: 900; }
  .cc-emit { text-align: center; margin: 3px 0; }
  .cc-emit .nome { font-size: 14px; font-weight: 900; }
  .cc-meta, .cc-periodo { text-align: center; margin: 4px 0; }
  .cc-meta .item, .cc-periodo .item { margin: 2px 0; }
  .cc-meta b, .cc-periodo b { font-weight: 900; }
  .cc-sep { border-top: 2px dashed #000; margin: 6px 0; }
  .cc-destaque { text-align: center; margin: 7px 0; }
  .cc-label { font-size: 12px; font-weight: 900; text-transform: uppercase; }
  .cc-valor { text-align: center; font-size: 20px; font-weight: 900; margin: 2px 0 6px; }
  .cc-obs { font-style: normal; font-weight: 900; text-align: center; margin: 6px 0; }
  .cc-assinatura { margin-top: 20px; text-align: center; font-weight: 900; }
  .cc-assinatura .linha { display: inline-block; border-top: 2px solid #000;
                          width: 66mm; padding-top: 2px; }
  .cc-resumo { margin: 6px 0; }
  .cc-resumo .item { display: flex; justify-content: space-between; gap: 8px; margin: 2px 0; }
  .cc-resumo .item span:last-child { white-space: nowrap; font-weight: 900; }
  .cc-resumo .total { font-weight: 900; border-top: 2px solid #000;
                      padding-top: 3px; margin-top: 3px; }
  .cc-diferenca { font-weight: 900; font-size: 14px; text-align: center;
                  border: 2px solid #000; padding: 4px; margin-top: 6px; }
  @media print {
    html, body { width: 72mm; color: #000 !important; }
  }
</style>
</head>
<body>
${corpo}
</body>
</html>`;
}

/**
 * Cabeçalho comum (emitente, terminal, operador).
 */
function _renderCabecalho({ emitente, terminal, operador, dataHora }) {
  const nome = emitente?.razao_social || emitente?.nome_fantasia || "Emitente não informado";
  const fantasia = emitente?.nome_fantasia && emitente?.razao_social ? emitente.nome_fantasia : null;
  const terminalNome = terminal?.nome_terminal || terminal?.uuid_terminal || terminal?.id_terminal_venda || "—";
  return `
    <div class="cc-emit">
      <div class="nome">${_escape(nome)}</div>
      ${fantasia ? `<div>${_escape(fantasia)}</div>` : ""}
      <div>CNPJ: ${_escape(_formatarCnpj(emitente?.cnpj))}</div>
    </div>
    <div class="cc-sep"></div>
    <div class="cc-meta">
      <div class="item">Terminal: <b>${_escape(terminalNome)}</b></div>
      <div class="item">Operador: <b>${_escape(operador?.nome || operador?.id_usuario || "—")}</b></div>
      <div class="item">Data/Hora: <b>${_escape(dataHora)}</b></div>
    </div>
  `;
}

function _renderAssinatura(label = "Assinatura do operador") {
  return `
    <div class="cc-assinatura">
      <div class="linha">&nbsp;</div>
      <div>${_escape(label)}</div>
    </div>
  `;
}

// ── Contexto: emitente/terminal/operador ────────────────────────────────────

async function _carregarContexto() {
  const api = getElectronAPI();
  const [emitente, terminalCfg, idUsuario, nomeUsuario] = await Promise.all([
    api.emitente.buscarEmitente().catch(() => null),
    AppConfigRepository.getTerminalConfig(),
    api.config.get("id_usuario"),
    api.config.get("nome_usuario").catch(() => null),
  ]);
  return {
    emitente,
    terminal: terminalCfg,
    operador: {
      id_usuario: idUsuario,
      nome:       nomeUsuario || (idUsuario ? `ID ${idUsuario}` : null),
    },
  };
}

function _contextoDoMovimento(ctx, movimento) {
  const payload = _safeParse(movimento?.payload_json) || {};
  const operadorPayload = payload.operador || {};

  return {
    ...ctx,
    terminal: {
      ...(ctx.terminal || {}),
      uuid_terminal: movimento?.uuid_terminal || payload.uuid_terminal || ctx.terminal?.uuid_terminal || null,
      id_terminal_venda: movimento?.id_terminal_venda || ctx.terminal?.id_terminal_venda || null,
      nome_terminal: movimento?.nome_terminal || ctx.terminal?.nome_terminal || null,
    },
    operador: {
      id_usuario: movimento?.id_usuario || operadorPayload.id_usuario || ctx.operador?.id_usuario || null,
      nome: movimento?.nome_usuario || operadorPayload.nome || ctx.operador?.nome || null,
    },
  };
}

function _usaTemplateAtual(html) {
  return String(html || "").includes(
    `name="caixa-comprovante-template" content="${COMPROVANTE_TEMPLATE_VERSION}"`
  );
}

// ── Geradores de HTML ───────────────────────────────────────────────────────

function gerarHtmlAbertura({ movimento, ctx }) {
  const payload = _safeParse(movimento.payload_json) || {};
  const obs     = payload.observacao || movimento.descricao || "";
  const corpo = `
    ${_renderCabecalho({
      emitente: ctx.emitente,
      terminal: ctx.terminal,
      operador: ctx.operador,
      dataHora: _formatarData(movimento.data_abertura || movimento.created_at),
    })}
    <div class="cc-title">Abertura de Caixa</div>
    <div class="cc-destaque">
      <div class="cc-label">Valor inicial</div>
      <div class="cc-valor">${_brl(movimento.valor_abertura)}</div>
    </div>
    ${obs ? `<div class="cc-sep"></div><div class="cc-obs">Obs.: ${_escape(obs)}</div>` : ""}
    ${_renderAssinatura()}
  `;
  return _wrap("Abertura de Caixa", corpo);
}

function gerarHtmlMovimentoSimples({ movimento, ctx, titulo }) {
  const obs = movimento.descricao || "";
  const corpo = `
    ${_renderCabecalho({
      emitente: ctx.emitente,
      terminal: ctx.terminal,
      operador: ctx.operador,
      dataHora: _formatarData(movimento.data_venda || movimento.created_at),
    })}
    <div class="cc-title">${_escape(titulo)}</div>
    <div class="cc-destaque">
      <div class="cc-label">Valor</div>
      <div class="cc-valor">${_brl(movimento.valor_movimento)}</div>
    </div>
    <div class="cc-sep"></div>
    <div class="cc-obs">Motivo/Obs.: ${_escape(obs || "—")}</div>
    ${_renderAssinatura()}
  `;
  return _wrap(titulo, corpo);
}

function gerarHtmlSuprimento(args) {
  return gerarHtmlMovimentoSimples({ ...args, titulo: "Suprimento de Caixa" });
}

function gerarHtmlSangria(args) {
  return gerarHtmlMovimentoSimples({ ...args, titulo: "Sangria de Caixa" });
}

function gerarHtmlFechamento({ movimento, ctx, aberturaMov }) {
  const payload = _safeParse(movimento.payload_json) || {};
  const resumo  = payload.resumo || {};
  const formas  = resumo.vendas_por_forma || {};
  const informado = Number(payload.valor_fechamento_informado) || Number(movimento.valor_movimento) || 0;
  const esperado  = Number(payload.valor_esperado) || 0;
  const diferenca = informado - esperado;
  const pagamentosInformados = payload.fechamento_pagamentos || null;
  const pagamentosEsperados = resumo.vendas_por_categoria || {};
  const labelsPagamento = {
    dinheiro: "Dinheiro",
    pix: "Pix",
    credito_loja: "Crédito loja",
    debito: "Débito",
    credito: "Crédito",
    beneficios: "Benefícios",
  };

  const formasHtml = Object.keys(formas).map((forma) => `
    <div class="item"><span>${_escape(forma)}</span><span>${_brl(formas[forma])}</span></div>
  `).join("");
  const conferenciaHtml = pagamentosInformados
    ? Object.keys(labelsPagamento).map((key) => {
      const valorEsperado = Number(pagamentosEsperados[key]) || 0;
      const valorInformado = Number(pagamentosInformados[key]) || 0;
      return `
        <div class="item"><span>${_escape(labelsPagamento[key])} esperado</span><span>${_brl(valorEsperado)}</span></div>
        <div class="item"><span>${_escape(labelsPagamento[key])} informado</span><span>${_brl(valorInformado)}</span></div>
        <div class="item"><span>Diferença ${_escape(labelsPagamento[key])}</span><span>${_brl(valorInformado - valorEsperado)}</span></div>
      `;
    }).join("")
    : "";

  const corpo = `
    ${_renderCabecalho({
      emitente: ctx.emitente,
      terminal: ctx.terminal,
      operador: ctx.operador,
      dataHora: _formatarData(movimento.data_fechamento || movimento.created_at),
    })}
    <div class="cc-title">Fechamento de Caixa</div>
    <div class="cc-periodo">
      <div class="item">Abertura: <b>${_escape(_formatarData(aberturaMov?.data_abertura))}</b></div>
      <div class="item">Fechamento: <b>${_escape(_formatarData(movimento.data_fechamento))}</b></div>
    </div>
    <div class="cc-sep"></div>

    <div class="cc-resumo">
      ${pagamentosInformados ? conferenciaHtml : `
        <div class="item"><span>Valor inicial</span><span>${_brl(resumo.abertura)}</span></div>
        ${formasHtml || `<div class="item"><span>Sem vendas</span><span>${_brl(0)}</span></div>`}
        <div class="item total"><span>Total vendido</span><span>${_brl(resumo.total_vendas)}</span></div>
        <div class="item"><span>Suprimentos</span><span>${_brl(resumo.suprimentos)}</span></div>
        <div class="item"><span>Sangrias</span><span>-${_brl(resumo.sangrias).replace("R$ ", "R$")}</span></div>
      `}
      <div class="item total"><span>Total esperado</span><span>${_brl(esperado)}</span></div>
      <div class="item"><span>Total informado</span><span>${_brl(informado)}</span></div>
      <div class="cc-diferenca">Diferença: ${_brl(diferenca)}</div>
    </div>

    ${payload.observacao ? `<div class="cc-sep"></div><div class="cc-obs">Obs.: ${_escape(payload.observacao)}</div>` : ""}

    ${_renderAssinatura()}
  `;
  return _wrap("Fechamento de Caixa", corpo);
}

function _safeParse(json) {
  try { return json ? JSON.parse(json) : null; } catch { return null; }
}

// ── API pública ─────────────────────────────────────────────────────────────

const CaixaComprovantePrintService = {
  gerarHtmlAbertura,
  gerarHtmlSuprimento,
  gerarHtmlSangria,
  gerarHtmlFechamento,

  /**
   * Verifica se a impressão automática de comprovantes está habilitada.
   */
  async impressaoAutomaticaHabilitada() {
    try {
      const v = await getElectronAPI().config.get("imprimir_comprovante_caixa_automatico");
      return v == null ? true : (v === "1" || v === 1 || v === true || v === "true");
    } catch {
      return true;
    }
  },

  /**
   * Gera o HTML adequado ao tipo do movimento, persiste em caixa_local
   * e dispara impressão automática (silenciosa) na impressora padrão.
   *
   * Em caso de falha de impressão, mantém o registro com
   * comprovante_impresso=0 e retorna { ok: false, error }.
   */
  async gerarEImprimir(uuid_movimento, { forcarImpressao = false } = {}) {
    if (!uuid_movimento) return { ok: false, error: "uuid_movimento obrigatório." };

    const movimento = await CaixaLocalRepository.buscarPorUuid(uuid_movimento);
    if (!movimento) return { ok: false, error: "Movimento de caixa não encontrado." };

    const ctx  = _contextoDoMovimento(await _carregarContexto(), movimento);
    const html = await this._gerarHtmlPorTipo(movimento, ctx);

    // Persiste o HTML antes de tentar imprimir
    await CaixaLocalRepository.salvarComprovante(uuid_movimento, html);

    // Enriquece payload_json com contexto de auditoria (operador, terminal, resumo)
    await this._enriquecerPayloadAuditoria(movimento, ctx);

    const auto = forcarImpressao || (await this.impressaoAutomaticaHabilitada());
    if (!auto) return { ok: true, impresso: false, html };

    const ret = await getElectronAPI().printer.imprimirComprovanteCaixa({
      uuid_movimento,
      html,
      reimpressao: false,
    });
    return { ...ret, html };
  },

  /**
   * Garante que o comprovante exista no modelo atual sem iniciar impressão.
   * Usado pela pré-visualização e pela reimpressão de registros legados.
   */
  async prepararComprovanteCaixa(uuid_movimento) {
    if (!uuid_movimento) return { ok: false, error: "uuid_movimento obrigatório." };

    const movimento = await CaixaLocalRepository.buscarPorUuid(uuid_movimento);
    if (!movimento) return { ok: false, error: "Movimento de caixa não encontrado." };

    if (_usaTemplateAtual(movimento.comprovante_html)) {
      return { ok: true, html: movimento.comprovante_html, atualizado: false };
    }

    const ctx = _contextoDoMovimento(await _carregarContexto(), movimento);
    const html = await this._gerarHtmlPorTipo(movimento, ctx);
    await CaixaLocalRepository.salvarComprovante(uuid_movimento, html);

    return { ok: true, html, atualizado: true };
  },

  /**
   * Reimprime usando o modelo atual e incrementa reimpressao_count.
   * Registros legados são atualizados antes da impressão.
   */
  async reimprimirComprovanteCaixa(uuid_movimento) {
    if (!uuid_movimento) return { ok: false, error: "uuid_movimento obrigatório." };

    const preparado = await this.prepararComprovanteCaixa(uuid_movimento);
    if (!preparado.ok) return preparado;

    return getElectronAPI().printer.imprimirComprovanteCaixa({
      uuid_movimento,
      html:        preparado.html,
      reimpressao: true,
    });
  },

  /**
   * Atalho usado por telas de “imprimir comprovante de caixa” em lote
   * (idêntico a reimprimir, mas semanticamente representa primeira impressão).
   */
  async imprimirComprovanteCaixa(uuid_movimento) {
    return this.reimprimirComprovanteCaixa(uuid_movimento);
  },

  // ── internos ──────────────────────────────────────────────────────────

  async _gerarHtmlPorTipo(movimento, ctx) {
    switch (Number(movimento.id_tipo)) {
      case CAIXA_TIPO.ABERTURA:
        return gerarHtmlAbertura({ movimento, ctx });
      case CAIXA_TIPO.SUPRIMENTO:
        return gerarHtmlSuprimento({ movimento, ctx });
      case CAIXA_TIPO.SANGRIA:
        return gerarHtmlSangria({ movimento, ctx });
      case CAIXA_TIPO.FECHAMENTO: {
        const irmaos = await CaixaLocalRepository.buscarMovimentosPorUuidCaixa(movimento.uuid_caixa);
        const aberturaMov = (irmaos || []).find((m) => Number(m.id_tipo) === CAIXA_TIPO.ABERTURA) || null;
        return gerarHtmlFechamento({ movimento, ctx, aberturaMov });
      }
      default:
        return _wrap("Comprovante de Caixa", `
          <div class="cc-title">Comprovante de Caixa</div>
          <div class="cc-row"><span>Tipo:</span><b>${_escape(movimento.id_tipo)}</b></div>
          <div class="cc-row"><span>Valor:</span><b>${_brl(movimento.valor_movimento)}</b></div>
        `);
    }
  },

  async _enriquecerPayloadAuditoria(movimento, ctx) {
    try {
      const payload = _safeParse(movimento.payload_json) || {};
      payload.operador = payload.operador || {
        id_usuario: ctx.operador?.id_usuario ?? null,
        nome:       ctx.operador?.nome ?? null,
      };
      payload.terminal = payload.terminal || {
        uuid_terminal:     ctx.terminal?.uuid_terminal ?? null,
        id_terminal_venda: ctx.terminal?.id_terminal_venda ?? null,
      };
      payload.impressao = {
        gerado_em: new Date().toISOString(),
      };
      // Não chamamos API direta para evitar criar campo novo no IPC;
      // o HTML já vai em comprovante_html, e o payload é atualizado no
      // próximo update natural do registro (sync). Para garantir, salvamos
      // o payload junto do comprovante via salvarComprovante (HTML)
      // sem tocar nos demais campos. Aqui apenas mantemos o contexto em
      // memória — a persistência de payload de auditoria ocorre no
      // momento da criação do registro em CaixaPdvDesktopService.
    } catch (_) {
      // não interrompe geração
    }
  },
};

export default CaixaComprovantePrintService;
